import { TemplateResult, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { GzSingleSelectWebComponent } from '../../gzSingleSelectDropdown';
import { SelectedOption } from '../../gzSingleSelectList/types';
import { styles } from '../view/styles';
import { isEmpty } from '@nirvana/shared/basic-types/object';

/**
 * This component is a single select dropdown that is used to select a phone code.
 * It is a wrapper around the GzSingleSelectListPhoneCode component.
 * It is used as a part of phone input component (GzPhoneInputWebComponent).
 */
@customElement('gz-single-select-phone-code')
export class GzSingleSelectPhoneCodeWebComponent extends GzSingleSelectWebComponent {
  @property({ type: Boolean, attribute: true, reflect: true })
  cannotDelete = true;

  @property({ type: Boolean, attribute: true, reflect: true })
  error = false;

  static get styles() {
    return styles;
  }

  protected renderSelectList(): TemplateResult {
    return html` <gz-single-select-list-phone-code
      id="single-selection-list"
      .filterOptions="${this.filterOptions}"
      searchPlaceholderString="${this.searchPlaceholderString}"
      noSearchResultsString="${this.noSearchResultsString}"
      @selectionChange="${this.handleListSelectionChange}"
      ?hasDynamicSearch="${this.hasDynamicSearch}"
      ?readonly="${this.readonly}"
      ?isLoading="${this.isLoading}"
      class="single-select-list"
      part="list"
    ></<gz-single-select-list-phone-code>`;
  }

  protected updateDropdownLabelWithSelectedValues(selectedOption: SelectedOption | null): void {
    if (this.dropdownMenu) {
      this.dropdownMenu.selectedValuesStrings = selectedOption ? selectedOption.label : '';
    }
  }

  /**
   * Function used to programatically set a value for this field
   * @param value
   */
  public setFormValue(value: string | number) {
    const optionExists: SelectedOption[] = this.filterOptions.filter(
      option => option.value === value || option.label === value
    );
    let selectedOption: SelectedOption | null = null;

    this.value = value;

    if (!isEmpty(optionExists)) {
      selectedOption = optionExists[0];
    }

    this.updateDropdownLabelWithSelectedValues(selectedOption);
    if (this.selectList) {
      this.selectList.selectedOption = selectedOption;
    }

    /*
     If this filter isn't used in forms, we don't want to emit the selection change event when we set it programmatically
     because we might mistake this event for a user manually changing the value.
     If the listener on this event isn't a validator, like on forms, but instead tries to re-renders this webcomponent,
     we might get stuck in an infinite loop
   */
    if (!this.isFilter && !this.selectList?.selectionEventEmitted) {
      this.selectList?.emitConfirmSelectionEvent();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-single-select-phone-code': GzSingleSelectPhoneCodeWebComponent;
  }
}
